import { Link } from "react-router";
import { useSetup } from "./setup-context";
import { Fragment, type ReactNode } from "react";
import { SquareCheck, SquareDashed } from "lucide-react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { names, ready } from "./state";
import { Correction } from "./shared";
function LiveConnectionError() {
  const { s, live } = useSetup();
  return (
    <>
      <p>{live.error}</p>
      {s.provider === "github" && (
        <>
          <Correction step="credentials">Review credentials</Correction>
          {" · "}
          <Correction step="app">Review app settings</Correction>
          <details className="mt-3">
            <summary>Review organization approval</summary>
            <p>
              Open your authorized OAuth apps, choose this app, and next to{" "}
              {s.values.org} select Request access → Request approval from
              owners. An organization owner approves it in Settings →
              Third-party Access → OAuth app policy → Review → Grant access.
            </p>
            <a
              href="https://github.com/settings/applications"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open GitHub application settings ↗
            </a>
            <p>Sign in again and retry the connection test.</p>
          </details>
          <details className="mt-3">
            <summary>Review SSO sign-in</summary>
            <p>
              Sign in to {s.values.org} through your organization’s identity
              provider, then sign in to GitHub again.
            </p>
            <a
              href={`https://github.com/${encodeURIComponent(s.values.org.trim())}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open organization ↗
            </a>
          </details>
        </>
      )}
      {live.recovery === "signin" && (
        <Button variant="secondary" size="sm" onClick={live.signIn}>
          <Button.Text>Sign in again ↗</Button.Text>
        </Button>
      )}
      {s.provider !== "github" && live.recovery === "credentials" && (
        <Correction step="credentials">Review credentials</Correction>
      )}
      {live.recovery === "permissions" && (
        <Correction step="app">Review Slack permissions</Correction>
      )}
      {live.recovery === "settings" && (
        <Link to={live.settingsHref ?? "../settings#authentication"}>
          Open identity settings
        </Link>
      )}
    </>
  );
}

export function ConnectionTask(): JSX.Element {
  const { s, dispatch, live } = useSetup();
  const available = ready(s);
  type Row = {
    label: string;
    done: boolean;
    status: string;
    action: string;
    available: boolean;
    step?: string;
    run?: () => void;
    pending?: boolean;
    hint?: string;
    error?: ReactNode;
  };
  const rows: Row[] = [
    {
      label: "App credentials",
      done: s.saved,
      status: "Saved",
      action: "Enter credentials",
      available: true,
      step: "credentials",
    },
    {
      label: `${names[s.provider]} sign-in`,
      done: s.authorized,
      status: "Signed in",
      action:
        live.error && live.errorStep === "signin" ? "Try again ↗" : "Sign in ↗",
      available,
      run: live.signIn,
      pending: live.pending === "signin",
      hint: "Save your connection credentials first.",
      error:
        live.error && live.errorStep === "signin" ? (
          <LiveConnectionError />
        ) : undefined,
    },
    {
      label: "Connection test",
      done: s.tested,
      status: "Passed",
      action:
        live.error && live.errorStep === "test"
          ? "Test again"
          : "Test connection",
      available: available && s.authorized,
      run: live.testConnection,
      pending: live.pending === "test",
      hint: `Sign in to ${names[s.provider]} first.`,
      error:
        live.error && live.errorStep === "test" ? (
          <LiveConnectionError />
        ) : undefined,
    },
  ];
  return (
    <>
      <table
        className="w-full border-collapse [&_tr>td:first-child]:pl-4"
        aria-label={`${names[s.provider]} connection actions`}
      >
        <tbody>
          {rows.map((row, index) => {
            const Icon = row.done ? SquareCheck : SquareDashed;
            return (
              <Fragment key={row.label}>
                <tr className={index === rows.length - 1 ? "" : "border-b"}>
                  <td
                    className={`py-8 pr-3 align-top ${index === 0 ? "pt-0" : ""} ${index === rows.length - 1 && !row.error ? "pb-0" : ""}`}
                  >
                    <div className="flex min-h-9 items-center gap-2.5 py-1.5 text-base">
                      <Icon
                        aria-hidden
                        className={`size-6 shrink-0 ${row.done ? "text-default-success" : "text-muted-foreground"}`}
                      />
                      <span>{row.label}</span>
                    </div>
                    {!row.done && !row.available && (
                      <p className="text-[13px] text-muted-foreground lg:ml-[34px]">
                        {row.hint}
                      </p>
                    )}
                  </td>
                  <td
                    className={`py-8 text-right align-top ${index === 0 ? "pt-0" : ""} ${index === rows.length - 1 && !row.error ? "pb-0" : ""}`}
                  >
                    {row.done ? (
                      <span className="flex h-9 items-center justify-end text-[13px] leading-none text-default-success">
                        {row.status}
                      </span>
                    ) : (
                      <Button
                        variant="secondary"
                        disabled={!row.available || !!live.pending}
                        onClick={() =>
                          row.step
                            ? dispatch({
                                type: "goto",
                                step: row.step,
                                correction: true,
                              })
                            : row.run?.()
                        }
                      >
                        {row.pending && live.pending === "signin"
                          ? `Waiting for ${names[s.provider]}…`
                          : row.pending && live.pending === "test"
                            ? "Testing…"
                            : row.action}
                      </Button>
                    )}
                    {row.pending && live.pending === "signin" && (
                      <Button variant="tertiary" onClick={live.cancelSignIn}>
                        <Button.Text>Cancel sign-in</Button.Text>
                      </Button>
                    )}
                  </td>
                </tr>
                {row.error && (
                  <tr>
                    <td colSpan={2} className="pb-8">
                      <Alert
                        variant="error"
                        className="[&_p]:mb-2.5 [&_p:last-child]:mb-0"
                      >
                        {row.error}
                      </Alert>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {live.toolCount != null && s.tested && (
            <tr>
              <td colSpan={2} className="pt-5 text-sm text-muted-foreground">
                Connected · {live.toolCount}{" "}
                {live.toolCount === 1 ? "tool" : "tools"} discovered. No tools
                were run.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}
